import { useState } from "react";
import client from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Search, Download, Loader2, CalendarDays } from "lucide-react";
import { fmtHHMM, agencyDate, agencyOffsetMinutes } from "@/lib/agencyTime";

// RECORDS — query all calls-for-service + DAR/business checks for a unit and/or
// location by date and compile everything into one exportable officer report.
export default function DailyActivity() {
  const [q, setQ] = useState({ unit: "", location: "", date: agencyDate() });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await client.get("/records/daily-activity", { params: { unit: q.unit || undefined, location: q.location || undefined, date: q.date || undefined, tz_offset: agencyOffsetMinutes() } });
      setRes(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Query failed"); }
    setBusy(false);
  };
  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await client.get("/records/daily-activity.pdf", { params: { unit: q.unit || undefined, location: q.location || undefined, date: q.date || undefined, tz_offset: agencyOffsetMinutes() }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `daily_activity_${q.unit || "all"}_${q.date || "all"}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Compiled report downloaded");
    } catch { toast.error("Export failed"); }
    setPdfBusy(false);
  };
  const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2 py-2 text-sm font-mono text-white outline-none focus:border-[#22D3EE] rounded-sm w-full";
  return (
    <Layout>
      <div className="p-4 max-w-5xl" data-testid="daily-activity-page">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays size={16} className="text-[#22D3EE]" />
          <span className="font-head font-bold text-sm text-white tracking-wider">DAILY ACTIVITY QUERY — RECORDS</span>
        </div>
        <div className="grid sm:grid-cols-4 gap-2 mb-3">
          <input data-testid="da-unit" value={q.unit} onChange={(e) => setQ({ ...q, unit: e.target.value })} placeholder="Unit (e.g. 200)" className={inp} />
          <input data-testid="da-location" value={q.location} onChange={(e) => setQ({ ...q, location: e.target.value })} placeholder="Location contains…" className={inp} />
          <input data-testid="da-date" type="date" value={q.date} onChange={(e) => setQ({ ...q, date: e.target.value })} className={inp} />
          <div className="flex gap-2">
            <button data-testid="da-run" onClick={run} disabled={busy}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-head font-bold bg-[#22D3EE] text-black rounded-sm disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} QUERY
            </button>
            <button data-testid="da-export" onClick={exportPdf} disabled={pdfBusy || !res}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-head font-bold text-white rounded-sm disabled:opacity-40" style={{ background: "#1d5d90" }}>
              {pdfBusy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} EXPORT PDF
            </button>
          </div>
        </div>
        {res && (
          <div className="space-y-3">
            <div className="border border-[#2C2D35] rounded-sm" data-testid="da-calls">
              <div className="px-3 py-2 bg-[#0d0e11] font-head text-xs font-bold text-[#22D3EE]">CALLS FOR SERVICE ({res.calls.length})</div>
              {res.calls.map((c) => (
                <div key={c.id} className="px-3 py-2 border-t border-[#2C2D35] flex flex-wrap items-center gap-x-3 gap-y-0.5" data-testid={`da-call-${c.cc_number}`}>
                  <span className="font-mono text-[11px] text-[#9CA3AF]">{fmtHHMM(c.created_at)} HRS</span>
                  <span className="font-mono text-[11px] font-bold text-[#67e8f9]">{c.cc_number}</span>
                  <span className="text-sm text-white">{c.call_type_label}</span>
                  <span className="font-mono text-[10px] text-[#9CA3AF]">{c.location?.address}</span>
                  <span className="ml-auto font-mono text-[10px] text-[#9CA3AF]">UNITS: {(c.units_history || []).join(", ") || "—"} · {c.disposition_code || c.status?.toUpperCase()}</span>
                </div>
              ))}
              {res.calls.length === 0 && <div className="px-3 py-3 font-mono text-[11px] text-[#6B7280] border-t border-[#2C2D35]">No calls matched.</div>}
            </div>
            <div className="border border-[#2C2D35] rounded-sm" data-testid="da-dars">
              <div className="px-3 py-2 bg-[#0d0e11] font-head text-xs font-bold text-[#F59E0B]">DAR / BUSINESS CHECKS ({res.dar_entries.length})</div>
              {res.dar_entries.map((d) => (
                <div key={d.id} className="px-3 py-2 border-t border-[#2C2D35]" data-testid={`da-dar-${d.id}`}>
                  <div className="flex flex-wrap items-center gap-x-3">
                    <span className="font-mono text-[11px] text-[#9CA3AF]">{fmtHHMM(d.ts)} HRS</span>
                    <span className="font-mono text-[11px] font-bold text-[#F59E0B]">{d.category}</span>
                    <span className="text-sm text-white">{d.location || "—"}</span>
                    <span className="ml-auto font-mono text-[10px] text-[#9CA3AF]">UNIT {d.unit || "—"} · {d.officer}{(d.photos || []).length ? ` · ${d.photos.length} photo(s)` : ""}</span>
                  </div>
                  {d.manager && <div className="font-mono text-[10px] text-[#9CA3AF]">SPOKE WITH: {d.manager}</div>}
                  {d.notes && <div className="text-[12px] text-[#C9CDD4]">{d.notes}</div>}
                </div>
              ))}
              {res.dar_entries.length === 0 && <div className="px-3 py-3 font-mono text-[11px] text-[#6B7280] border-t border-[#2C2D35]">No DAR entries matched.</div>}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
